import {
	AbstractControl,
	ValidationErrors,
	Validators,
	FormGroup,
} from '@angular/forms';
import {
	PersonnelClient,
	FieldWorker,
	InvoiceWithCutoffRequest,
	DateRange,
	Job,
} from '../api';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import {
	map,
	switchMap,
	distinctUntilChanged,
	debounceTime,
} from 'rxjs/operators';
import {
	isEmptyInputValue,
	isValidNorthAmericanState,
	isValidUnitedOrCanadianState,
} from '../utilities/utility-functions';
import { parsePhoneNumber, PhoneNumber } from 'libphonenumber-js';

export class CustomValidators {
	static hasValidId(control: AbstractControl) {
		const value = control.value;

		if (value) {
			return value.id ? null : { invalidItem: true };
		}
		return null;
	}

	static mustBeNumeric(control: AbstractControl): ValidationErrors {
		const value = control.value;

		if (value) {
			return isNaN(value) ? { isNotNumeric: true } : null;
		}

		return null;
	}

	static isNonNegativeNumber(control: AbstractControl) {
		const value = control.value;

		if (isEmptyInputValue(value)) {
			return null;
		}

		return isNaN(value) || value < 0 ? { invalidNumber: true } : null;
	}

	static isValidState(control: AbstractControl) {
		const value = control.value;

		if (value) {
			return isValidUnitedOrCanadianState(value)
				? null
				: { invalidItem: true };
		}
		return null;
	}

	static isValidNorthAmericanState(control: AbstractControl) {
		const value = control.value;

		if (value) {
			return isValidNorthAmericanState(value)
				? null
				: { invalidItem: true };
		}
		return null;
	}

	static endDateNotBeforeStart(control: AbstractControl): ValidationErrors {
		const value: Job = (control as FormGroup).getRawValue();
		if (value) {
			const start = moment(value.start);
			const end = moment(value.end);

			if (start.isAfter(end)) {
				control.get('end').setErrors({ endDateBeforeStart: true });
			} else {
				control.get('end').setErrors(null);
			}
		}
		return null;
	}

	static cutoffEndIsNotBeforeLastCutoff(lastCutoff: string) {
		return (control: AbstractControl): ValidationErrors | null => {
			const value: InvoiceWithCutoffRequest = (
				control as FormGroup
			).getRawValue();
			if (value) {
				const cutoffEnd = moment.utc(value.cutoffEndDate);
				const lastCutoffEnd = moment.utc(lastCutoff);

				if (cutoffEnd.isBefore(lastCutoffEnd)) {
					return { endIsBeforeLast: true };
				}
			}
			return null;
		};
	}

	static time(control: AbstractControl) {
		const errors = Validators.pattern(
			'^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$'
		)(control);

		return errors ? { invalidTimeFormat: true } : null;
	}

	static doesNotStartOrEndBeforeToday(control: AbstractControl) {
		const dateRange: DateRange = control.value;

		if (dateRange) {
			const start = moment.utc(dateRange.begin);
			const end = moment.utc(dateRange.end);

			return start.isBefore(moment.utc()) || end.isBefore(moment.utc())
				? { doesNotStartOrEndBeforeToday: true }
				: null;
		}
		return null;
	}

	static isNotBeforeToday(control: AbstractControl) {
		const value = moment.utc(control.value).endOf('day');

		if (value) {
			return value.isBefore(moment.utc())
				? { valueBeforeToday: true }
				: null;
		}
		return null;
	}

	static isNotAfterToday(control: AbstractControl) {
		const value = moment.utc(control.value).endOf('day');
		return !value
			? null
			: value.isAfter(moment.utc().endOf('day'))
			? { valueAfterToday: true }
			: null;
	}

	static isPossiblyValidPhone(control: AbstractControl) {
		const value = control.value ? control.value.toString() : undefined;
		if (value) {
			let number: PhoneNumber;
			try {
				if (value.startsWith('+')) {
					number = parsePhoneNumber(value);
				} else {
					number = parsePhoneNumber(value, 'US');
				}
				if (!number.isPossible()) {
					return { badPhoneNumber: true };
				}
			} catch (error) {
				return { badPhoneNumber: true };
			}
		}
		return null;
	}

	static isEmailListInvalid(control: AbstractControl) {
		const emails = control.value ? control.value : undefined;

		const emailRegex =
			/([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})/;
		if (emails) {
			const hasInvalidEmail = emails.some(
				(email: string) => !emailRegex.test(email)
			);
			if (hasInvalidEmail) {
				return { hasInvalidEmails: true };
			}
			return null;
		}
		return null;
	}

	static isEmployeeCodeUnique(personnelClient: PersonnelClient, id: number) {
		return (
			control: AbstractControl
		): Observable<ValidationErrors | null> => {
			return control.valueChanges.pipe(
				debounceTime(250),
				distinctUntilChanged(),
				map(
					(value: string) =>
						({
							id: id,
							employeeCode: value,
						} as FieldWorker)
				),
				switchMap((v) => personnelClient.isEmployeeCodeUnique(v)),
				map((isUnique) => (!isUnique ? { notUnique: true } : null))
			);
		};
	}

	static isPaycomIdUnique(personnelClient: PersonnelClient, id: number) {
		return (
			control: AbstractControl
		): Observable<ValidationErrors | null> => {
			return control.valueChanges.pipe(
				debounceTime(250),
				distinctUntilChanged(),
				map(
					(value: string) =>
						({
							id: id,
							paycomId: value,
						} as FieldWorker)
				),
				switchMap((v) => personnelClient.isPaycomIdUnique(v)),
				map((isUnique) => (!isUnique ? { notUnique: true } : null))
			);
		};
	}
}
